import { useState, useEffect, useMemo } from "react";

import {
  fetchPlans,
  createPlan,
  updatePlan,
  deletePlan,
} from "../../../api/admin/coreBasics";

import { swalSuccess, swalError, swalConfirm } from "../../../utils/swalAlert.js";
import EditModal from "../components/EditModal.jsx";

import "../../../styles/AdminPanel/CoreBasic.css"

const EMPTY_FORM = {
  title: "",
  price: "",
  duration_days: "",
  allow_registry_only: false,
  is_active: true,
};

const PAGE_SIZE = 8;

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(1);

  const load = async () => {
    const res = await fetchPlans();
    setPlans(res.data);
  };

  useEffect(() => {
    load();
  }, []);

  const totalCount = plans.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);

  useEffect(() => {
    if (page !== pageSafe) setPage(pageSafe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  const pageNumbers = useMemo(() => {
    const nums = [];
    for (let n = 1; n <= totalPages; n++) nums.push(n);
    return nums;
  }, [totalPages]);

  const paged = useMemo(() => {
    const start = (pageSafe - 1) * PAGE_SIZE;
    return plans.slice(start, start + PAGE_SIZE);
  }, [plans, pageSafe]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setIsModalOpen(true);
  };

  const openEdit = (plan) => {
    setEditing(plan);

    // فقط فیلدهایی که واقعاً توی فرم استفاده می‌شن رو می‌گیریم
    // تا آبجکت اضافه (plan_id و ...) به بکند فرستاده نشه
    setForm({
      title: plan.title,
      price: String(Math.floor(Number(plan.price))).replace(
        /\B(?=(\d{3})+(?!\d))/g,
        ",",
      ),
      duration_days: plan.duration_days.toString(),
      allow_registry_only: !!plan.allow_registry_only,
      is_active: !!plan.is_active,
    });

    setIsModalOpen(true);
  };

  // جداکننده ۳ رقمی برای قیمت
  const formatPrice = (value) => {
    let cleaned = value.replace(/[^\d]/g, "");
    return cleaned.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const save = async () => {
    if (!form.title.trim() || !form.price || !form.duration_days) {
      swalError({
        title: "اطلاعات ناقص",
        text: "عنوان، قیمت و مدت اعتبار الزامی است",
      });
      return;
    }

    const payload = {
      title: form.title,
      price: Number(form.price.replace(/,/g, "")),
      duration_days: Number(form.duration_days),
      allow_registry_only: !!form.allow_registry_only,
      is_active: !!form.is_active,
    };

    try {
      if (editing) {
        await updatePlan(editing.plan_id, payload);
      } else {
        await createPlan(payload);
      }

      await swalSuccess({ title: "با موفقیت ذخیره شد" });

      setIsModalOpen(false);
      load();
    } catch (err) {
      swalError({ title: "خطا در ذخیره" });
    }
  };

  const remove = async (id) => {
    const confirmed = await swalConfirm({
      title: "حذف پلن؟",
      text: "این عملیات قابل بازگشت نیست",
      confirmText: "بله حذف شود",
      danger: true,
    });

    if (!confirmed) return;

    try {
      await deletePlan(id);
      await swalSuccess({ title: "پلن حذف شد" });
      load();
    } catch {
      swalError({ title: "خطا در حذف" });
    }
  };

  return (
    <>
      <div className="page-header-row">
        <h2 className="page-title">پلن‌های فروش</h2>

        <div className="add-button-wrapper">
          <button className="btn-glass" onClick={openAdd}>
            افزودن پلن
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="user-table">
          <thead>
            <tr>
              <th>#</th>
              <th>عنوان</th>
              <th>قیمت</th>
              <th>مدت (روز)</th>
              <th>فقط ثبت نام؟</th>
              <th>فعال؟</th>
              <th>عملیات</th>
            </tr>
          </thead>

          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center" }}>
                  داده‌ای وجود ندارد
                </td>
              </tr>
            ) : (
              paged.map((p, i) => (
                <tr key={p.plan_id}>
                  <td>{(pageSafe - 1) * PAGE_SIZE + i + 1}</td>
                  <td>{p.title}</td>
                  <td>{Number(p.price).toLocaleString()}</td>
                  <td>{p.duration_days}</td>
                  <td>
                    {p.allow_registry_only ? (
                      <span className="status-badge status-active">فقط ثبت‌نام</span>
                    ) : (
                      <span className="status-badge status-inactive">آزاد</span>
                    )}
                  </td>
                  <td>
                    {p.is_active ? (
                      <span className="status-badge status-active">فعال</span>
                    ) : (
                      <span className="status-badge status-inactive">غیرفعال</span>
                    )}
                  </td>

                  <td className="actions-cell">
                    <button
                      className="btn-icon btn-icon-edit"
                      title="ویرایش"
                      onClick={() => openEdit(p)}
                    >
                      <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                      </svg>
                    </button>

                    <button
                      className="btn-icon btn-icon-delete"
                      title="حذف"
                      onClick={() => remove(p.plan_id)}
                    >
                      <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18" />
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalCount > 0 && (
          <div className="orders-pagination">
            <span className="pagination-info">
              {totalCount.toLocaleString("fa-IR")} پلن — صفحه{" "}
              {pageSafe.toLocaleString("fa-IR")} از{" "}
              {totalPages.toLocaleString("fa-IR")}
            </span>
            <div className="pagination-controls">
              <button
                className="page-btn"
                disabled={pageSafe === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="صفحه قبل"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>
              {pageNumbers.map((n) => (
                <button
                  key={n}
                  className={`page-btn number${n === pageSafe ? " active" : ""}`}
                  onClick={() => setPage(n)}
                >
                  {n.toLocaleString("fa-IR")}
                </button>
              ))}
              <button
                className="page-btn"
                disabled={pageSafe === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="صفحه بعد"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <EditModal onClose={() => setIsModalOpen(false)}>
          <h3>{editing ? "ویرایش پلن" : "افزودن پلن"}</h3>

          <input
            type="text"
            placeholder="عنوان پلن"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />

          <input
            type="text"
            placeholder="قیمت (مثال: 120,000)"
            value={form.price}
            onChange={(e) =>
              setForm({ ...form, price: formatPrice(e.target.value) })
            }
          />

          <input
            type="text"
            placeholder="مدت اعتبار (روز)"
            value={form.duration_days}
            onChange={(e) =>
              setForm({
                ...form,
                duration_days: e.target.value.replace(/[^\d]/g, ""),
              })
            }
          />

          <label className="checkbox-glass">
            <input
              type="checkbox"
              checked={form.allow_registry_only}
              onChange={(e) =>
                setForm({ ...form, allow_registry_only: e.target.checked })
              }
            />
            فقط ثبت نام
          </label>

          <label className="checkbox-glass">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) =>
                setForm({ ...form, is_active: e.target.checked })
              }
            />
            فعال
          </label>

          <div className="modal-buttons">
            <button className="btn" onClick={save}>
              ذخیره
            </button>

            <button
              className="btn btn-cancel"
              onClick={() => setIsModalOpen(false)}
            >
              انصراف
            </button>
          </div>
        </EditModal>
      )}
    </>
  );
}
